/**
 * Frontend API functions for Planning Board
 *
 * IMPORTANT: Only import types from .types.ts files, never from endpoint files.
 */
import { fetchEndpointData } from '../../utils/fetchEndpointData';
import type { PlanningBoardTrucksResponse } from './endpoints/trucks/PlanningBoardTrucksEndpoint.types';
import type { PlanningBoardBacklogResponse } from './endpoints/orders/PlanningBoardBacklogEndpoint.types';
import type { PlanningBoardAssignOrderResponse } from './endpoints/orders/PlanningBoardAssignOrderEndpoint.types';
import type { PlanningBoardReorderResponse } from './endpoints/orders/PlanningBoardReorderEndpoint.types';
import type { PlanningBoardSendOrderResponse } from './endpoints/orders/PlanningBoardSendOrderEndpoint.types';
import type { PlanningBoardUpdateStatusResponse } from './endpoints/orders/PlanningBoardUpdateStatusEndpoint.types';
import type { PlanningBoardUpdateOrderKmResponse } from './endpoints/orders/PlanningBoardUpdateOrderKmEndpoint.types';
import type { PlanningBoardSuggestLoadsResponse } from './endpoints/orders/PlanningBoardSuggestLoadsEndpoint.types';
import type { PlanningBoardReorderTrucksResponse } from './endpoints/trucks/PlanningBoardReorderTrucksEndpoint.types';
import type { PlanningBoardUpdateTruckDriverResponse } from './endpoints/trucks/PlanningBoardUpdateTruckDriverEndpoint.types';
import type { Region } from './endpoints/shared.types';
import type { PlanningBoardWeeklyResponse } from './endpoints/weekly/PlanningBoardWeeklyEndpoint.types';
import type {
  PlanningBoardWeeklySaveDayBody,
  PlanningBoardWeeklySaveDayResponse,
} from './endpoints/weekly/PlanningBoardWeeklySaveDayEndpoint.types';
import type {
  PlanningBoardWeeklyTargetBody,
  PlanningBoardWeeklyTargetResponse,
} from './endpoints/weekly/PlanningBoardWeeklyTargetEndpoint.types';

// Re-export types for convenience
export type {
  PlanningBoardTrucksResponse,
  PlanningBoardTruck,
  PlanningBoardTruckOrder,
  PlanningBoardTruckOrderPurchase,
} from './endpoints/trucks/PlanningBoardTrucksEndpoint.types';
export type {
  PlanningBoardBacklogResponse,
  PlanningBoardBacklogOrder,
} from './endpoints/orders/PlanningBoardBacklogEndpoint.types';
export type { Region } from './endpoints/shared.types';

// ============================================================================
// Trucks
// ============================================================================

export async function getPlanningBoardTrucks(date: string, plannerId?: string): Promise<PlanningBoardTrucksResponse> {
  const params = new URLSearchParams({ date });
  if (plannerId) {
    params.set('plannerId', plannerId);
  }
  return fetchEndpointData<PlanningBoardTrucksResponse>(`/api/v1/planning-board/trucks?${params.toString()}`);
}

// ============================================================================
// Orders - Backlog
// ============================================================================

export interface GetBacklogOptions {
  date: string;
  dateType?: 'load' | 'unload';
  region?: Region;
  search?: string;
}

export async function getPlanningBoardBacklog(options: GetBacklogOptions): Promise<PlanningBoardBacklogResponse> {
  const params = new URLSearchParams({ date: options.date });
  if (options.dateType) {
    params.set('dateType', options.dateType);
  }
  if (options.region) {
    params.set('region', options.region);
  }
  if (options.search) {
    params.set('search', options.search);
  }
  return fetchEndpointData<PlanningBoardBacklogResponse>(`/api/v1/planning-board/orders/backlog?${params.toString()}`);
}

// ============================================================================
// Orders - Assign
// ============================================================================

export interface AssignOrderOptions {
  orderId: string;
  supplierTruckId: string | null;
  date: string;
  sequence?: number;
}

export async function assignPlanningBoardOrder(options: AssignOrderOptions): Promise<PlanningBoardAssignOrderResponse> {
  return fetchEndpointData<PlanningBoardAssignOrderResponse>('/api/v1/planning-board/orders/assign', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Orders - Reorder
// ============================================================================

export interface ReorderOrdersOptions {
  supplierTruckId: string;
  date: string;
  orderSequences: Array<{
    orderId: string;
    sequence: number;
  }>;
}

export async function reorderPlanningBoardOrders(options: ReorderOrdersOptions): Promise<PlanningBoardReorderResponse> {
  return fetchEndpointData<PlanningBoardReorderResponse>('/api/v1/planning-board/orders/reorder', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Orders - Send
// ============================================================================

export interface SendOrdersOptions {
  orderIds: string[];
  supplierTruckId: string;
  date: string;
  contactIds: number[];
  /** Complete carrier cost set per order — it replaces the order's purchase lines. */
  orderCosts: Array<{
    orderId: string;
    lines: Array<{
      productTypeId: string;
      amount: number;
      unitPrice: number;
      externalNote?: string;
    }>;
  }>;
  planningNotes?: string;
}

export async function sendPlanningBoardOrders(options: SendOrdersOptions): Promise<PlanningBoardSendOrderResponse> {
  return fetchEndpointData<PlanningBoardSendOrderResponse>('/api/v1/planning-board/orders/send', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Orders - Update Status
// ============================================================================

export interface UpdateOrderStatusOptions {
  orderId: string;
  status: string;
}

export async function updatePlanningBoardOrderStatus(
  options: UpdateOrderStatusOptions,
): Promise<PlanningBoardUpdateStatusResponse> {
  return fetchEndpointData<PlanningBoardUpdateStatusResponse>('/api/v1/planning-board/orders/status', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Orders - Update driven kilometers
// ============================================================================

export interface UpdateOrderKmOptions {
  orderId: string;
  manualFullKm: number | null;
  manualEmptyKm: number | null;
}

export async function updatePlanningBoardOrderKm(
  options: UpdateOrderKmOptions,
): Promise<PlanningBoardUpdateOrderKmResponse> {
  return fetchEndpointData<PlanningBoardUpdateOrderKmResponse>('/api/v1/planning-board/orders/km', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Orders - Suggest Loads
// ============================================================================

export interface SuggestLoadsOptions {
  truckId: string;
  date: string;
  limit?: number;
}

export async function suggestPlanningBoardLoads(
  options: SuggestLoadsOptions,
): Promise<PlanningBoardSuggestLoadsResponse> {
  const params = new URLSearchParams({ truckId: options.truckId, date: options.date });
  if (options.limit) {
    params.set('limit', options.limit.toString());
  }
  return fetchEndpointData<PlanningBoardSuggestLoadsResponse>(
    `/api/v1/planning-board/orders/suggest?${params.toString()}`,
  );
}

// ============================================================================
// Trucks - Reorder
// ============================================================================

export interface ReorderTrucksOptions {
  trucks: Array<{ id: string; sortIdx: number }>;
}

export async function reorderPlanningBoardTrucks(
  options: ReorderTrucksOptions,
): Promise<PlanningBoardReorderTrucksResponse> {
  return fetchEndpointData<PlanningBoardReorderTrucksResponse>('/api/v1/planning-board/trucks/reorder', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Trucks - Update driver display name
// ============================================================================

export interface UpdateTruckDriverOptions {
  truckId: string;
  driverDisplayName: string;
}

export async function updatePlanningBoardTruckDriver(
  options: UpdateTruckDriverOptions,
): Promise<PlanningBoardUpdateTruckDriverResponse> {
  return fetchEndpointData<PlanningBoardUpdateTruckDriverResponse>('/api/v1/planning-board/trucks/driver', {
    method: 'POST',
    body: options,
  });
}

// ============================================================================
// Weekly board
// ============================================================================

export type {
  PlanningBoardWeeklyResponse,
  WeeklyTrailer,
  WeeklyTruck,
  WeeklyTrip,
  WeeklyTripStatus,
} from './endpoints/weekly/PlanningBoardWeeklyEndpoint.types';
export type { PlanningBoardWeeklySaveDayResponse };

export async function getPlanningBoardWeekly(weekStart: string): Promise<PlanningBoardWeeklyResponse> {
  const params = new URLSearchParams({ weekStart });
  return fetchEndpointData<PlanningBoardWeeklyResponse>(`/api/v1/planning-board/weekly?${params.toString()}`);
}

export async function savePlanningBoardWeeklyDay(
  body: PlanningBoardWeeklySaveDayBody,
): Promise<PlanningBoardWeeklySaveDayResponse> {
  return fetchEndpointData<PlanningBoardWeeklySaveDayResponse>('/api/v1/planning-board/weekly/day', {
    method: 'PUT',
    body,
  });
}

export async function setPlanningBoardWeeklyTarget(
  body: PlanningBoardWeeklyTargetBody,
): Promise<PlanningBoardWeeklyTargetResponse> {
  return fetchEndpointData<PlanningBoardWeeklyTargetResponse>('/api/v1/planning-board/weekly/target', {
    method: 'PUT',
    body,
  });
}
